import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

export interface Widget {
  id: string;
  name: string;
  text: string;
  categoryId: string;
  type: 'pie' | 'line' | 'bar' | 'text';
  chartData?: {
    labels: string[];
    values: number[];
  };
}

export interface Category {
  id: string;
  name: string;
  widgets: Widget[];
}

interface DashboardState {
  categories: Category[];
  timeFilter: 'last1day' | 'last2days' | 'last24hours';
  searchTerm: string;
}

// Load initial state from localStorage if available
const loadFromLocalStorage = (): DashboardState | undefined => {
  try {
    const serializedState = localStorage.getItem('dashboardState');
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return undefined;
  }
};

const defaultState: DashboardState = {
  categories: [
    {
      id: 'cspm',
      name: 'CSPM Executive Dashboard',
      widgets: []
    },
    {
      id: 'cwpp',
      name: 'CWPP Dashboard',
      widgets: []
    },
    {
      id: 'registry',
      name: 'Registry Scan Dashboard',
      widgets: []
    }
  ],
  timeFilter: 'last2days',
  searchTerm: ''
};

const initialState: DashboardState = loadFromLocalStorage() || defaultState;

// Save to localStorage helper
const saveToLocalStorage = (state: DashboardState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('dashboardState', serializedState);
  } catch (err) {
    // Ignore write errors
  }
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    addWidget: (state, action: PayloadAction<{ 
      categoryId: string; 
      name: string; 
      text: string; 
      type: 'pie' | 'line' | 'bar' | 'text';
      chartData?: {
        labels: string[];
        values: number[];
      };
    }>) => {
      const category = state.categories.find(cat => cat.id === action.payload.categoryId);
      if (category) {
        const newWidget: Widget = {
          id: uuidv4(),
          name: action.payload.name,
          text: action.payload.text,
          categoryId: action.payload.categoryId,
          type: action.payload.type,
          chartData: action.payload.chartData
        };
        category.widgets.push(newWidget);
      }
      saveToLocalStorage(state);
    },
    removeWidget: (state, action: PayloadAction<string>) => {
      state.categories.forEach(category => {
        category.widgets = category.widgets.filter(widget => widget.id !== action.payload);
      });
      saveToLocalStorage(state);
    },
    updateWidget: (state, action: PayloadAction<{ id: string; name: string; text: string }>) => {
      state.categories.forEach(category => {
        const widget = category.widgets.find(w => w.id === action.payload.id);
        if (widget) {
          widget.name = action.payload.name;
          widget.text = action.payload.text;
        }
      });
      saveToLocalStorage(state);
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setTimeFilter: (state, action: PayloadAction<'last1day' | 'last2days' | 'last24hours'>) => {
      state.timeFilter = action.payload;
      saveToLocalStorage(state);
    }
  }
});

export const { addWidget, removeWidget, updateWidget, setSearchTerm, setTimeFilter } = dashboardSlice.actions;
export default dashboardSlice.reducer;
